import { getCurrentBusinessId, readBestBusinessMergedPayload } from './sharedCore';
import { readLotsSyncForBusiness } from './lotsReadSource';
import { calculateDaysUntilExpiry } from './dateCalculations';
import { getLotsForProduct, LOT_STATUS, isLotExpired } from './lotSystem';
import {
  EXPIRATION_THRESHOLDS,
  isExpiringSoon as isExpiringSoonByConservation
} from '../domain/inventory/expiry';
import { kvGet, kvSet, kvRemove, kvKeysStartingWith } from '../infrastructure/storage/kvAccess';
import {
  buildExpiryMovement,
  recordStockMovements
} from '../domain/inventory/movements';
import { deriveExpiredProductsFromLots } from '../domain/inventory/expiredView';
import { publishLegacyWindowEvent } from '../infrastructure/events/legacyEventBridge';
import { sameProductId } from './productIdUtils';

/** @param {unknown} raw */
function parseStoredJsonRecord(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

export { EXPIRATION_THRESHOLDS };

export const isProductExpired = (expirationDate) => {
  if (!expirationDate) return false;
  return isLotExpired({ expirationDate });
};

export const getDaysUntilExpiration = (expirationDate) => {
  if (!expirationDate) return null;
  return calculateDaysUntilExpiry(expirationDate);
};

/** @deprecated Prefer domain/inventory/expiry.isExpiringSoon — kept for callers. */
export const shouldShowExpirationAlert = (conservation, expirationDate) =>
  isExpiringSoonByConservation({ conservation, expirationDate });

function resolveAllLots(products, lotsFromContext, businessId = null) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: true });

  if (Array.isArray(lotsFromContext) && lotsFromContext.length > 0) {
    return lotsFromContext;
  }

  if (bid) {
    const fromEntity = readLotsSyncForBusiness(bid);
    if (fromEntity.length > 0) {
      return fromEntity;
    }
  }

  let allLots = getLotsForProduct(null, null, lotsFromContext);

  if (allLots.length === 0 && lotsFromContext) {
    allLots = Array.isArray(lotsFromContext)
      ? lotsFromContext
      : Object.values(lotsFromContext);
  }

  if (allLots.length === 0 && bid) {
    const { parsed } = readBestBusinessMergedPayload(bid);
    if (parsed?.lots) {
      allLots = Array.isArray(parsed.lots)
        ? parsed.lots
        : Object.values(parsed.lots);
    }
  }

  return allLots;
}

function isDeletedExpiredMarker(productId, expirationDate, location) {
  const deletedKey = `deleted_expired_${productId}_${expirationDate}_${location}`;
  const raw = kvGet(deletedKey);
  return raw != null && raw !== '' && parseStoredJsonRecord(raw);
}

function shouldProcessExpiredLot(lot, products) {
  if (!lot?.expirationDate) return false;
  if (lot.status === LOT_STATUS.EXPIRED) return false;
  if (Number(lot.remainingQuantity) <= 0) return false;
  if (!isLotExpired(lot)) return false;

  const product = (Array.isArray(products) ? products : []).find((prod) =>
    sameProductId(prod?.id ?? prod?.productId, lot.productId)
  );
  // Lot has a real expiry date past due — process even if catalog row is missing.
  // Only skip when the matched product is explicitly without date.
  if (product?.conservation === 'SANS_DATE') return false;

  const loc = lot.location || 'Principal';
  if (isDeletedExpiredMarker(lot.productId, lot.expirationDate, loc)) {
    return false;
  }

  // Stale "already processed" markers must not block ACTIVE lots that still have qty
  // (e.g. after a lot rebuild restored stock without clearing the marker).
  const processedKey = `processed_expired_lot_${lot.id}`;
  const alreadyProcessedRaw = kvGet(processedKey);
  if (alreadyProcessedRaw != null && alreadyProcessedRaw !== '') {
    if (lot.status === LOT_STATUS.ACTIVE || !lot.status) {
      // allow re-process
    } else {
      return false;
    }
  }

  return true;
}

/**
 * LOT → EXPIRED via EXPIRY movement projection (ADR-0016 Phase 5 residual).
 * No dual-write: quantity/status only from InventoryMovement.
 */
export async function processExpiredLotsSweep({
  businessId = null,
  purchases = [],
  products = [],
  sales = [],
  lots: lotsFromContext = null
} = {}) {
  const bid = businessId || getCurrentBusinessId({ allowFallback: true });
  const allLots = resolveAllLots(products, lotsFromContext, bid);
  const expiredLots = allLots.filter((lot) => shouldProcessExpiredLot(lot, products));

  const movements = [];
  const processedProductIds = new Set();
  const processedEntries = [];
  let processedCount = 0;

  for (const lot of expiredLots) {
    try {
      const product = (Array.isArray(products) ? products : []).find((prod) =>
        sameProductId(prod?.id ?? prod?.productId, lot.productId)
      );

      const qtyBefore = Number(lot.remainingQuantity) || 0;
      if (qtyBefore <= 0) continue;

      movements.push(
        buildExpiryMovement(
          {
            productId: lot.productId,
            quantity: qtyBefore,
            lotId: lot.id,
            stockLocation: lot.location || 'Principal',
            purchaseUnitPrice: lot.purchaseUnitPrice,
            batchNumber: lot.batchNumber,
            expiredProductName: product?.name || ''
          },
          lot,
          { note: 'expired_product' }
        )
      );

      processedCount += 1;
      processedProductIds.add(lot.productId);
      processedEntries.push({
        productId: lot.productId,
        location: lot.location ?? null
      });
    } catch {
      // Einzellot-Fehler blockiert nicht den gesamten Sweep
    }
  }

  if (bid && movements.length > 0) {
    const { generateCorrelationId, resolveMovementActorMeta } = await import(
      '../domain/inventory/movements'
    );
    const actorMeta = resolveMovementActorMeta(bid, {
      actorId: 'system:expiry_sweep'
    });
    const batchCorr = generateCorrelationId(`expiry_${Date.now()}`);
    await recordStockMovements(
      bid,
      movements.map((m) => ({
        ...m,
        correlationId: m.correlationId || batchCorr,
        expiredProductName: m.expiredProductName || m.note
      })),
      {
        businessId: bid,
        defaultCorrelationId: batchCorr,
        projectLots: true,
        actorId: actorMeta.actorId || 'system:expiry_sweep',
        sessionId: actorMeta.sessionId || null
      }
    );

    for (const m of movements) {
      if (!m?.lotId) continue;
      const processedKey = `processed_expired_lot_${m.lotId}`;
      kvSet(processedKey, {
        processedAt: new Date().toISOString(),
        quantity: Math.abs(Number(m.quantity) || 0),
        lotId: m.lotId
      });
    }
  }

  if (bid && processedCount > 0) {
    const freshLots = readLotsSyncForBusiness(bid);
    const lotsForUi = freshLots.length ? freshLots : allLots;

    const { runStockRecalculationAfterMovement } = await import(
      '../domain/inventory/recalculation'
    );
    const recalcResult = await runStockRecalculationAfterMovement({
      businessId: bid,
      entries: processedEntries,
      lots: lotsForUi,
      source: 'expiry',
      showInventoryOverlay: false
    });

    const { refreshStockDataReadCache } = await import('./stockDataReadSource');
    await refreshStockDataReadCache(bid);

    // Orchestrator already warms stock+inventory — only fallback if warm skipped
    if (!recalcResult?.stockWarmed) {
      try {
        const { afterStockMovementCommit } = await import('../services/cacheReadModel');
        afterStockMovementCommit('expiry_processed');
      } catch {
        /* ignore */
      }
    }

    publishLegacyWindowEvent('stockUpdated', {
      source: 'expiry',
      forceRefresh: true,
      timestamp: Date.now()
    });
    publishLegacyWindowEvent('inventoryUpdated', {
      source: 'expiry',
      forceRefresh: true,
      timestamp: Date.now()
    });

    const { triggerCacheUpdateAfterExpiration } = await import('./cacheUpdateTrigger');
    const derived = deriveExpiredProductsFromLots(lotsForUi, products);
    for (const row of derived.slice(-processedCount)) {
      triggerCacheUpdateAfterExpiration(row);
    }
  }

  return {
    processedCount,
    movementCount: movements.length,
    processedProductIds: Array.from(processedProductIds),
    derivedExpired: deriveExpiredProductsFromLots(allLots, products)
  };
}

/**
 * @deprecated Nutzt processExpiredLotsSweep — expiredProducts[] wird nicht mehr als Wahrheit geschrieben.
 */
export const processExpiredProducts = (
  purchases,
  products,
  sales,
  expiredProducts,
  setExpiredProducts,
  setProducts,
  setPurchases,
  businessId = null,
  lotsFromContext = null
) => {
  void processExpiredLotsSweep({
    businessId,
    purchases,
    products,
    sales,
    lots: lotsFromContext
  }).then((result) => {
    if (result.processedCount > 0 && typeof setExpiredProducts === 'function') {
      const { parsed } = readBestBusinessMergedPayload(businessId || getCurrentBusinessId());
      const lots = parsed?.lots
        ? Array.isArray(parsed.lots)
          ? parsed.lots
          : Object.values(parsed.lots)
        : [];
      const derived = deriveExpiredProductsFromLots(lots, parsed?.products || products);
      setExpiredProducts(derived);
    }
  });

  return {
    newExpiredProducts: [],
    updatedProducts: products,
    updatedPurchases: purchases
  };
};

export const cleanupDeletedExpiredProducts = () => {
  try {
    const deletedKeys = kvKeysStartingWith('deleted_expired_');
    const processedKeys = kvKeysStartingWith('processed_expired_');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    let cleanedCount = 0;

    deletedKeys.forEach((key) => {
      try {
        const deletedInfo = parseStoredJsonRecord(kvGet(key));
        if (deletedInfo?.deletedAt) {
          const deletedDate = new Date(deletedInfo.deletedAt);
          if (deletedDate < thirtyDaysAgo) {
            kvRemove(key);
            cleanedCount++;
          }
        }
      } catch {
        kvRemove(key);
        cleanedCount++;
      }
    });

    processedKeys.forEach((key) => {
      try {
        const processedInfo = parseStoredJsonRecord(kvGet(key));
        if (processedInfo?.processedAt) {
          const processedDate = new Date(processedInfo.processedAt);
          if (processedDate < ninetyDaysAgo) {
            kvRemove(key);
            cleanedCount++;
          }
        }
      } catch {
        kvRemove(key);
        cleanedCount++;
      }
    });
  } catch {
    // ignore
  }
};

export const deleteExpiredProduct = (expiredProductId, expiredProducts, setExpiredProducts) => {
  const updatedExpiredProducts = expiredProducts.filter((ep) => ep.id !== expiredProductId);
  setExpiredProducts(updatedExpiredProducts);
};

export const getFilteredExpiredProducts = (expiredProducts, startDate, endDate) => {
  if (!startDate && !endDate) return expiredProducts;

  return expiredProducts.filter((ep) => {
    const expiredDate = new Date(ep.expiredAt);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && end) {
      return expiredDate >= start && expiredDate <= end;
    }
    if (start) {
      return expiredDate >= start;
    }
    if (end) {
      return expiredDate <= end;
    }

    return true;
  });
};
